import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Session Fingerprinting Service
 * 
 * Creates a browser fingerprint at login time and validates it on each request.
 * If the fingerprint changes (e.g., session cookie stolen and used from a different browser),
 * the session is immediately invalidated.
 *
 * This detects session hijacking where an attacker steals the auth cookie
 * but uses it from a different browser/device.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionFingerprintService {

  private readonly FINGERPRINT_KEY = 'sf_hash';
  private currentFingerprint: string | null = null;

  constructor(private authService: AuthService) {
    // When user logs in, generate and store fingerprint
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        this.onLogin();
      } else {
        this.onLogout();
      }
    });
  }

  /**
   * Generate fingerprint on login and store it.
   */
  private onLogin(): void {
    const fp = this.generateFingerprint();
    const stored = sessionStorage.getItem(this.FINGERPRINT_KEY);

    if (!stored) {
      // First time — store the fingerprint
      sessionStorage.setItem(this.FINGERPRINT_KEY, fp);
      this.currentFingerprint = fp;
    } else if (stored !== fp) {
      // Fingerprint mismatch — possible session hijacking
      console.error('[SessionFingerprint] Browser fingerprint mismatch detected. Forcing logout.');
      sessionStorage.removeItem(this.FINGERPRINT_KEY);
      this.authService.logout();
    } else {
      this.currentFingerprint = fp;
    }
  }

  private onLogout(): void {
    sessionStorage.removeItem(this.FINGERPRINT_KEY);
    this.currentFingerprint = null;
  }

  /**
   * Validate that the current browser still matches the login fingerprint.
   * Call this periodically or before sensitive operations.
   */
  validateFingerprint(): boolean {
    const stored = sessionStorage.getItem(this.FINGERPRINT_KEY);
    if (!stored) return true; // No fingerprint stored = not logged in or first visit

    const current = this.generateFingerprint();
    return stored === current;
  }

  /**
   * Get the current fingerprint hash for sending to backend.
   */
  getFingerprint(): string {
    if (!this.currentFingerprint) {
      this.currentFingerprint = this.generateFingerprint();
    }
    return this.currentFingerprint;
  }

  /**
   * Generate a deterministic browser fingerprint based on stable browser properties.
   * This is NOT unique per user — it identifies the browser environment.
   */
  private generateFingerprint(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth.toString(),
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '0',
      (navigator as any).deviceMemory?.toString() || '0',
      this.getCanvasFingerprint(),
    ];

    return this.hashString(components.join('|'));
  }

  /**
   * Canvas fingerprinting — draws invisible text and hashes the result.
   * Different browsers/GPUs produce slightly different results.
   */
  private getCanvasFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      if (!ctx) return 'no-canvas';

      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('TGAA-fp', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('TGAA-fp', 4, 17);

      return canvas.toDataURL().slice(-50); // Last 50 chars of data URL
    } catch {
      return 'canvas-error';
    }
  }

  /**
   * Simple string hash (DJB2 algorithm).
   */
  private hashString(str: string): string {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit
    }
    return Math.abs(hash).toString(36);
  }
}
